import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, FormGroup, Label, Input, InputGroup } from 'reactstrap';
import { useMutation } from 'react-apollo';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash-es';
import { CustomButton } from '../../../containers';
import { DocumentLink } from '../../../components/DocumentLink';
import { formatDate } from '../../../constants/dateFormatting';
import {
  POLICY_CODE_AO,
  POLICY_CODE_EU,
  POLICY_CODE_PZ,
  POLICY_CODE_HE,
  POLICY_CAR_LIABILITY,
  POLICY_HEALTH_TRAVEL,
  POLICY_HEALTH,
  POLICY_OTHER,
  POLICY_HOK_EXPRESS,
} from '../../../constants/business';
import { AdminReprintPolicyMutation } from '../../../operations/mutations/AdminUserTransactionMutation';
import { handleRequestError } from '../../../constants/errorCodes';
import { saveFileToDevice } from '../../../utils/generateFile';
import { useAORisksDefinitionsQuery } from '../../../utils/customHooks';

export const PolicyInfoSection = React.memo(({ confirmationCode, referenceNumber, documents, payload }) => {
  const dispatch = useDispatch();

  const { risks } = useAORisksDefinitionsQuery({ skip: payload.product !== POLICY_CODE_AO });

  const [rePrintPolicy] = useMutation(AdminReprintPolicyMutation, {
    variables: { paymentId: confirmationCode },
    onError: error => handleRequestError(error, dispatch),
    onCompleted: data => {
      const invoiceData = get(data, 'reprintInsurancePolicy', {});
      if (invoiceData.base64) {
        saveFileToDevice(`polica-${referenceNumber}.pdf`, invoiceData.base64);
      }
    },
  });

  const insuranceStartDate = payload.insuranceStartDate ? formatDate(payload.insuranceStartDate) : null;
  const insuranceEndDate = payload.insuranceEndDate ? formatDate(payload.insuranceEndDate) : null;

  const insuranceDuration = insuranceStartDate + (insuranceEndDate ? ` - ${insuranceEndDate}` : '');

  const insuredPeople = payload.insuredPeople ? payload.insuredPeople : [payload.insuredPerson];

  const getProduct = useMemo(() => {
    switch (payload.product) {
      case POLICY_CODE_AO:
        return POLICY_CAR_LIABILITY;
      case POLICY_CODE_EU:
        return POLICY_HEALTH;
      case POLICY_CODE_PZ:
        return POLICY_HEALTH_TRAVEL;
      case POLICY_CODE_HE:
        return POLICY_HOK_EXPRESS;
      default:
        return POLICY_OTHER;
    }
  }, [payload.product]);

  const generateInsureeDataOutput = insuree => {
    const insureeRole =
      insuree.mainInsuredPerson !== null && typeof insuree.mainInsuredPerson !== 'undefined' ? (
        insuree.mainInsuredPerson ? (
          <b>GLAVNI OSIGURANIK</b>
        ) : (
          'DODATNI OSIGURANIK'
        )
      ) : (
        ''
      );
    const name = `${insuree.name || ''} ${insuree.surename || ''}`.trim() || insuree.OsiNaz || '';
    const oib = insuree.oib ? `, ${insuree.oib}` : insuree.OsiId ? `, ${insuree.OsiId}` : '';
    const dateOfBirth = insuree.dateOfBirth ? `, ${formatDate(insuree.dateOfBirth)}` : '';
    const zip = insuree.zip ? `, ${insuree.zip}` : insuree.OsiPtt ? `, ${insuree.OsiPtt}` : '';
    const place = insuree.city ? `, ${insuree.city}` : insuree.OsiMjesto ? `, ${insuree.OsiMjesto}` : '';
    const address = insuree.address ? `, ${insuree.address}` : insuree.OsiAdr ? `, ${insuree.OsiAdr}` : '';
    const phoneNumber = insuree.OsiTel ? `, ${insuree.OsiTel}` : '';
    const email = insuree.email || '';

    return (
      <div key={insuree.oib || insuree.OsiId}>
        {insureeRole} {name}
        {oib}
        {dateOfBirth}
        {zip}
        {place}
        {address}
        {phoneNumber}
        {email}
      </div>
    );
  };

  const generateInsuredItemOutput = () => {
    if (payload.product === POLICY_CODE_AO || payload.product === POLICY_CODE_HE) {
      const vehicleData = get(payload, 'vehicleData', {});

      const vehicleTechSpecs = get(vehicleData, 'vrstaVozilaNaziv', '')
        ? get(vehicleData, 'vrstaVozilaNaziv', '').includes('L')
          ? `${get(vehicleData, 'obujamMotora', '')} ccm`
          : `${get(vehicleData, 'snagaMotora', '')} kW`
        : '';

      return `${vehicleData.markaVozilaNaziv}, ${vehicleData.tipVozilaNaziv}, ${vehicleData.godinaProizvodnje}, ${vehicleTechSpecs}, ${vehicleData.brojSasije}, ${vehicleData.regOznaka}`;
    }
    return '';
  };

  const generateAdditionalItemsOutput = () => {
    if (payload.product === POLICY_CODE_AO) {
      const additionalRisks = get(payload, 'allContractedRisks', []);
      if (risks.length > 0) {
        const displayRisks = get(payload, 'allContractedRisks', []).map(item =>
          get(
            risks.find(risk => risk.riskCode === item),
            'name',
            item
          )
        );
        return displayRisks.join(', ');
      } else {
        if (additionalRisks.length > 0) {
          return additionalRisks.reduce((previousValue, risk) => `${previousValue}, ${risk}`);
        }
        return '';
      }
    }
  };

  const generateProductOutput = () => {
    if (payload.product === POLICY_CODE_EU) {
      return {
        EU01: 'EUREKA 1',
        EU02: 'EUREKA 2',
        EU03: 'EUREKA 3',
      }[payload.priceList];
    }
    if (payload.product === POLICY_CODE_PZ) {
      const pzItems = payload.risk.split(',');
      const product = {
        PZ11: 'PUTNO A',
        PZ12: 'PUTNO B',
        PZ13: 'PUTNO C',
      }[pzItems[0]];
      const risk = {
        '0007': 'NEZGODA A',
        '0008': 'NEZGODA B',
        '0009': 'NEZGODA C',
      }[payload.kategorijaNezgode];
      return product + (risk ? `, ${risk}` : '').trim();
    }
  };

  return (
    <Row className="no-gutters my-4">
      <Col xs="12">
        <Row className="no-gutters">
          <Col xs="12">
            <h3 className="mb-4">
              <FormattedMessage id="adminUserTransactionsPage.policyInfo" />
            </h3>
          </Col>
        </Row>
        <FormGroup row>
          <Col xs="12" sm="6">
            <Label for="policyNumber">
              <FormattedMessage id="inputs.policyNumber" />
            </Label>
            <InputGroup>
              <Input className="hok-standard-input" id="policyNumber" readOnly defaultValue={referenceNumber} />
              <CustomButton
                className="hok-dashboard-btn d-none d-xl-inline-block ml-0 ml-sm-3"
                block={false}
                translationId="downloadPolicy"
                disabled={!referenceNumber}
                onClick={referenceNumber ? rePrintPolicy : () => null}
              />
            </InputGroup>
          </Col>
          <Col xs="12" sm="6" className="d-flex align-items-end">
            <CustomButton
              className="hok-dashboard-btn d-block w-xs-100 d-sm-inline-block d-xl-none mb-0 mb-sm-3"
              block={false}
              translationId="downloadPolicy"
              disabled={!referenceNumber}
              onClick={referenceNumber ? rePrintPolicy : () => null}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col xs="12" sm="6">
            <Label for={insuranceEndDate ? 'policyDuration' : 'insuranceStartDate'}>
              <FormattedMessage id={`inputs.${insuranceEndDate ? 'policyDuration' : 'insuranceStartDate'}`} />
            </Label>
            <InputGroup>
              <Input
                id={insuranceEndDate ? 'policyDuration' : 'insuranceStartDate'}
                readOnly
                defaultValue={insuranceDuration}
              />
            </InputGroup>
          </Col>
          <Col xs="12" sm="6">
            <Label for="productType">
              <FormattedMessage id="inputs.productType" />
            </Label>
            <InputGroup>
              <Input id="productType" className="text-uppercase" readOnly defaultValue={getProduct} />
            </InputGroup>
          </Col>
        </FormGroup>
        {(payload.product === POLICY_CODE_EU || payload.product === POLICY_CODE_PZ) && (
          <FormGroup row>
            <Col xs="12">
              <Label for="product">
                <FormattedMessage id="inputs.product" />
              </Label>
              <InputGroup>
                <Input id="product" readOnly defaultValue={generateProductOutput()} />
              </InputGroup>
            </Col>
          </FormGroup>
        )}
        {(payload.product === POLICY_CODE_AO || payload.product === POLICY_CODE_HE) && (
          <FormGroup row>
            <Col xs="12">
              <Label for="policyItem">
                <FormattedMessage id="inputs.policyItem" />
              </Label>
              <InputGroup>
                <Input id="policyItem" readOnly defaultValue={generateInsuredItemOutput()} />
              </InputGroup>
            </Col>
          </FormGroup>
        )}
        {payload.product === POLICY_CODE_AO && (
          <FormGroup row>
            <Col xs="12">
              <Label for="policyAdditionalRisks">
                <FormattedMessage id="inputs.policyAdditionalRisks" />
              </Label>
              <InputGroup>
                <Input id="policyAdditionalRisks" readOnly value={generateAdditionalItemsOutput()} />
              </InputGroup>
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Col xs="12">
            <Label for="insuredPeople">
              <FormattedMessage id="inputs.insuredPeople" />
            </Label>
            <InputGroup>
              {/* <Input
                id="insuredPeople"
                type="textarea"
                readOnly
                defaultValue={insuredPeople.map(insuree => generateInsureeDataOutput(insuree))}
              /> */}
              <div className="hok-user-transactions-box">
                {insuredPeople.map(insuree => generateInsureeDataOutput(insuree))}
              </div>
            </InputGroup>
          </Col>
        </FormGroup>
        {documents.length > 0 && (
          <Row className="mb-4">
            <Col xs="12">
              <Label>
                <FormattedMessage id="adminUserTransactionsPage.policyDocumentation" />
              </Label>
            </Col>
            {documents.map(document => (
              <Col className="d-flex align-items-center my-3" sm="6" key={document.content_id}>
                <DocumentLink content={document.filename} link={get(document, 'AWS.Location', '')} />
              </Col>
            ))}
          </Row>
        )}
      </Col>
    </Row>
  );
});
